import {
  Button,
  Callout,
  Card,
  Checkbox,
  FormGroup,
  HTMLTable,
  InputGroup,
  Intent,
  Slider,
} from "@blueprintjs/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminQuery } from "../../api/AdminClient";
import { useMutation } from "../../api/useMutation";
import { AdminErrorMessage } from "../../shared/AdminErrorMessage";
import { getPath as getReportDetailsPath } from "../report/details/ReportDetailsPath";
import type { GeneratorFixtureUser, GeneratorFixtureVehicle } from "./fixtureModels";

const USERS_PATH = "admin/reportGenerator/fixtures/users";
const VEHICLES_PATH = "admin/reportGenerator/fixtures/vehicles";

const MAX_COUNT = 100;

interface GeneratePartySelection {
  fixtureUserId: string;
  nationalIdentityNumber: string | null;
}

interface GenerateReportsRequest {
  parties: GeneratePartySelection[];
  vehicleIds: string[];
  twoPartyPercentage: number;
  signedPercentage: number;
  signingMethodPercentage: number;
  overduePercentage: number;
  overdueDaysMin: number | null;
  overdueDaysMax: number | null;
  count: number;
}

function percentLabel(value: number, endpoints: [string, string]): string {
  if (value === 0) return endpoints[0];
  if (value === 100) return endpoints[1];
  return `${value}%`;
}

export function GeneratorPanel() {
  const users = useAdminQuery<GeneratorFixtureUser[]>(USERS_PATH);
  const vehicles = useAdminQuery<GeneratorFixtureVehicle[]>(VEHICLES_PATH);

  const [checkedUserIds, setCheckedUserIds] = useState<Set<string>>(new Set());
  const [ninByUserId, setNinByUserId] = useState<Record<string, string>>({});
  const [checkedVehicleIds, setCheckedVehicleIds] = useState<Set<string>>(new Set());

  const [twoPartyPercentage, setTwoPartyPercentage] = useState(0);
  const [signedPercentage, setSignedPercentage] = useState(0);
  const [signingMethodPercentage, setSigningMethodPercentage] = useState(0);
  const [overduePercentage, setOverduePercentage] = useState(0);
  const [overdueDaysMin, setOverdueDaysMin] = useState(10);
  const [overdueDaysMax, setOverdueDaysMax] = useState(30);
  const [count, setCount] = useState(1);

  const generate = useMutation<GenerateReportsRequest, string[]>({
    path: "admin/reportGenerator/generate",
    method: "POST",
    body: {
      parties: Array.from(checkedUserIds).map((fixtureUserId) => ({
        fixtureUserId,
        nationalIdentityNumber: ninByUserId[fixtureUserId] || null,
      })),
      vehicleIds: Array.from(checkedVehicleIds),
      twoPartyPercentage,
      signedPercentage,
      signingMethodPercentage,
      overduePercentage,
      overdueDaysMin: overduePercentage > 0 ? overdueDaysMin : null,
      overdueDaysMax: overduePercentage > 0 ? overdueDaysMax : null,
      count,
    },
  });

  function toggleUser(id: string) {
    setCheckedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleVehicle(id: string) {
    setCheckedVehicleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const twoPartyRequired = twoPartyPercentage > 0;
  const canGenerate =
    checkedUserIds.size >= (twoPartyRequired ? 2 : 1) &&
    checkedVehicleIds.size >= (twoPartyRequired ? 2 : 1) &&
    !generate.result.loading;

  return (
    <div>
      <h3>Users</h3>
      {users.error ? <AdminErrorMessage>{users.error.message}</AdminErrorMessage> : null}
      {users.data && users.data.length === 0 ? (
        <Callout>No saved users yet — add some on the Users tab.</Callout>
      ) : null}
      {users.data && users.data.length > 0 ? (
        <HTMLTable striped>
          <thead>
            <tr>
              <th />
              <th>Name</th>
              <th>Customer number</th>
              <th>Phone</th>
              <th>NIN (this run only)</th>
            </tr>
          </thead>
          <tbody>
            {users.data.map((user) => (
              <tr key={user.customerGuid}>
                <td>
                  <Checkbox checked={checkedUserIds.has(user.customerGuid)} onChange={() => toggleUser(user.customerGuid)} />
                </td>
                <td>
                  {user.firstName} {user.lastName}
                </td>
                <td>{user.customerNumber}</td>
                <td>{user.phone}</td>
                <td>
                  {checkedUserIds.has(user.customerGuid) ? (
                    <InputGroup
                      value={ninByUserId[user.customerGuid] ?? ""}
                      onChange={(e) => setNinByUserId({ ...ninByUserId, [user.customerGuid]: e.target.value })}
                    />
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </HTMLTable>
      ) : null}

      <h3>Vehicles</h3>
      {vehicles.error ? <AdminErrorMessage>{vehicles.error.message}</AdminErrorMessage> : null}
      {vehicles.data && vehicles.data.length === 0 ? (
        <Callout>No saved vehicles yet — add some on the Vehicles tab.</Callout>
      ) : null}
      {vehicles.data && vehicles.data.length > 0 ? (
        <HTMLTable striped>
          <thead>
            <tr>
              <th />
              <th>License plate</th>
              <th>Vehicle type</th>
              <th>Manufacturer</th>
              <th>Model</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.data.map((vehicle) => (
              <tr key={vehicle.id}>
                <td>
                  <Checkbox
                    checked={checkedVehicleIds.has(vehicle.id)}
                    onChange={() => toggleVehicle(vehicle.id)}
                  />
                </td>
                <td>{vehicle.licensePlateNumber}</td>
                <td>{vehicle.vehicleType}</td>
                <td>{vehicle.manufactureName}</td>
                <td>{vehicle.modelName}</td>
                <td>{vehicle.comment}</td>
              </tr>
            ))}
          </tbody>
        </HTMLTable>
      ) : null}

      <h3>Settings</h3>

      <div style={{ maxWidth: "480px" }}>
        <Card style={{ marginBottom: "1rem" }}>
          <h4>Signature</h4>
          <FormGroup label="% Signed">
            <div style={{ padding: "0 3.5rem" }}>
              <Slider
                min={0}
                max={100}
                stepSize={1}
                labelValues={[0, 100]}
                labelRenderer={(value) => percentLabel(value, ["Unsigned", "Signed"])}
                value={signedPercentage}
                onChange={setSignedPercentage}
              />
            </div>
          </FormGroup>
          <FormGroup label="Signing method — % BankID">
            <div style={{ padding: "0 3.5rem" }}>
              <Slider
                min={0}
                max={100}
                stepSize={1}
                labelValues={[0, 100]}
                labelRenderer={(value) => percentLabel(value, ["Handwritten", "BankID"])}
                value={signingMethodPercentage}
                onChange={setSigningMethodPercentage}
                disabled={signedPercentage === 0}
              />
            </div>
          </FormGroup>
        </Card>

        <Card style={{ marginBottom: "1rem" }}>
          <h4>Archival</h4>
          <FormGroup label="% Overdue">
            <div style={{ padding: "0 3.5rem" }}>
              <Slider
                min={0}
                max={100}
                stepSize={1}
                labelValues={[0, 100]}
                labelRenderer={(value) => percentLabel(value, ["Normal", "Overdue"])}
                value={overduePercentage}
                onChange={setOverduePercentage}
              />
            </div>
          </FormGroup>
          <FormGroup label="Overdue days (min)" labelFor="overdueDaysMin" inline>
            <InputGroup
              id="overdueDaysMin"
              type="number"
              min={1}
              disabled={overduePercentage === 0}
              value={String(overdueDaysMin)}
              onChange={(e) => setOverdueDaysMin(Math.max(1, ~~Number(e.target.value)))}
            />
          </FormGroup>
          <FormGroup label="Overdue days (max)" labelFor="overdueDaysMax" inline>
            <InputGroup
              id="overdueDaysMax"
              type="number"
              min={1}
              disabled={overduePercentage === 0}
              value={String(overdueDaysMax)}
              onChange={(e) => setOverdueDaysMax(Math.max(1, ~~Number(e.target.value)))}
            />
          </FormGroup>
        </Card>

        <Card style={{ marginBottom: "1rem" }}>
          <h4>Parties</h4>
          <FormGroup label="% Two-party">
            <div style={{ padding: "0 3.5rem" }}>
              <Slider
                min={0}
                max={100}
                stepSize={1}
                labelValues={[0, 100]}
                labelRenderer={(value) => percentLabel(value, ["Single-party", "Two-party"])}
                value={twoPartyPercentage}
                onChange={setTwoPartyPercentage}
              />
            </div>
          </FormGroup>
        </Card>

        <FormGroup label="Count" labelFor="count" inline>
          <InputGroup
            id="count"
            type="number"
            min={1}
            max={MAX_COUNT}
            value={String(count)}
            onChange={(e) => setCount(Math.min(MAX_COUNT, Math.max(1, ~~Number(e.target.value))))}
          />
        </FormGroup>
      </div>

      <Button
        intent={Intent.PRIMARY}
        disabled={!canGenerate}
        loading={generate.result.loading}
        onClick={() => generate.fetch()}
      >
        Generate
      </Button>
      {generate.result.error ? (
        <AdminErrorMessage>{generate.result.error.message}</AdminErrorMessage>
      ) : null}
      {generate.result.data ? (
        <Callout intent={Intent.SUCCESS} style={{ marginTop: "1rem" }}>
          <p>Generated {generate.result.data.length} report(s):</p>
          <ul>
            {generate.result.data.map((id) => (
              <li key={id}>
                <Link to={getReportDetailsPath(id)}>{id}</Link>
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}
    </div>
  );
}
